<svelte:options runes={false} />
<script>
  // Shishito trailing-stop ladder (TASK-K2T-8): every holding by distance-to-stop (EXIT-2:
  // stop = peak − trail once peak clears the arm threshold), nearest first. A STALE BAR SILENTLY
  // DISARMS the stops — the panel says so loudly instead of pretending protection. Room is drawn as
  // a bar AND printed as a number (never encode by geometry alone).
  import { fmtDrift, displaySymbol as display } from '../../../lib/integrity.js';

  export let shishito = null; // ShishitoIntegrity

  // Presentation-only attention threshold: a rung with less than this much room renders in the danger
  // tone (paired with the printed number — never color alone).
  const NEAR_ROOM = 0.03;

  $: ladder = shishito?.stopLadder ?? [];
  // The room bar's full scale: the widest known room (min 5%) so relative distance stays readable.
  $: maxRoom = Math.max(0.05, ...ladder.map((r) => r.roomToStop ?? 0));
  const pct = (v) => (v == null ? '—' : fmtDrift(v));
</script>

{#if !shishito}
  <!-- host renders this panel only for Shishito -->
{:else}
  {#if !shishito.barsFresh}
    <div class="alert alert-danger" role="alert">
      Bars are stale — trailing stops are DISARMED until fresh bars arrive (a stale bar silently disarms the exit engine).
    </div>
  {/if}
  {#if ladder.length === 0}
    <div class="empty-state"><p class="empty-state-description">No holdings — nothing on the ladder.</p></div>
  {:else}
    <table>
      <thead>
        <tr><th>Symbol</th><th>Stop</th><th class="num">Stop level</th><th class="num">Room to stop</th><th class="it-room-col" aria-hidden="true"></th></tr>
      </thead>
      <tbody>
        {#each ladder as r (r.symbol)}
          <tr class:it-near={r.armed && r.roomToStop != null && r.roomToStop < NEAR_ROOM}>
            <td>{display(r.symbol)}</td>
            <td>
              {#if r.armed}<span class="it-armed">ARMED</span>
              {:else}<span class="it-unarmed">unarmed</span>{/if}
            </td>
            <td class="num">{pct(r.stopLevel)}</td>
            <td class="num">{pct(r.roomToStop)}</td>
            <td class="it-room-col">
              {#if r.roomToStop != null}
                <span class="it-room-track"><span class="it-room-bar" class:it-room-near={r.roomToStop < NEAR_ROOM}
                  style={`width:${Math.max(2, Math.min(100, (Math.max(0, r.roomToStop) / maxRoom) * 100))}%`}></span></span>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
    <p class="it-ladder-note num">
      arm &gt; {fmtDrift(shishito.armThreshold)} peak · trail {fmtDrift(shishito.trailDrawdown)} — derived from entry decisions + cached closes
    </p>
  {/if}
{/if}

<style>
  table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  th, td { padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left; }
  th { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .it-armed { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.06em; padding: 0.1rem 0.35rem; border-radius: 3px; background: rgba(26, 162, 96, 0.15); color: var(--success, #1aa260); }
  .it-unarmed { font-size: 0.62rem; color: var(--text-muted, #8a8b8d); }
  .it-near td { background: rgba(224, 83, 58, 0.06); }
  .it-room-col { width: 30%; min-width: 120px; }
  .it-room-track { display: block; height: 8px; background: var(--surface-2, #14151a); border-radius: 3px; overflow: hidden; }
  .it-room-bar { display: block; height: 100%; border-radius: 3px; background: var(--success, #1aa260); }
  .it-room-bar.it-room-near { background: var(--danger, #e0533a); }
  .it-ladder-note { margin: 0.4rem 0 0; font-size: 0.64rem; color: var(--text-muted, #8a8b8d); }
  .num { font-variant-numeric: tabular-nums; }
</style>
